import { Folder } from "lucide-react"
import type { Space } from "@/hooks/use-spaces"
import { normalizeExplorerPath } from "@/lib/explorer-tree-focus"
import { getDocumentDisplayIcon } from "@/lib/document-icons"
import type { SidebarShortcut } from "@/lib/api"
import type { Workspace } from "@/providers/workspace-provider"

/** Synthetic extension id for expand/collapse of per-workspace Pinned subtree */
export const SIDEBAR_PINS_EXPAND_ID = 'pinned-sidebar' as const

export function expandKeyForPinnedWorkspace(workspaceId: string) {
  return `${workspaceId}::${SIDEBAR_PINS_EXPAND_ID}` as const
}

/** Group pins under a sidebar workspace (= org row). Prefer content_root path over organization_id. */
export function shortcutBelongsToWorkspace(shortcut: SidebarShortcut, ws: Workspace): boolean {
  const root = (ws.contentRoot ?? '').replace(/\/$/, '').trim()
  const p = normalizeExplorerPath(String(shortcut.folder_path ?? shortcut.document_path ?? ''))
  const n = normalizeExplorerPath(root)

  // Path wins when present. Legacy personal/* rows still carry hop-studios organization_id;
  // org_id-only matching wrongly parks those pins under Hop.
  if (p && n) {
    return p === n || p.startsWith(`${n}/`)
  }

  if (shortcut.organization_id) return shortcut.organization_id === ws.id
  return false
}

export function pinnedShortcutsForWorkspace(shortcuts: SidebarShortcut[], ws: Workspace): SidebarShortcut[] {
  return shortcuts
    .filter((s) => shortcutBelongsToWorkspace(s, ws))
    .sort(
      (a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    )
}

/** Same payload Documents / My documents tree rows send via `open-context-menu`. */
export function pinnedShortcutContextMenuPayload(
  shortcut: SidebarShortcut,
  workspaceId: string,
): {
  type: 'document' | 'folder'
  value: string
  path?: string
  filePath?: string
  documentId?: string
  workspaceId: string
} {
  const value =
    shortcut.label ||
    shortcut.document_title ||
    shortcut.folder_path?.split('/').filter(Boolean).pop() ||
    'Untitled'
  if (shortcut.folder_path) {
    return {
      type: 'folder',
      value,
      path: shortcut.folder_path,
      workspaceId,
    }
  }
  return {
    type: 'document',
    value,
    path: shortcut.document_path,
    filePath: shortcut.document_path,
    documentId: shortcut.document_id ?? undefined,
    workspaceId,
  }
}

/** Resolve workspace id for reorder scope (explicit org vs path heuristic) */
export function resolvePinnedWorkspaceIdForShortcut(
  shortcut: SidebarShortcut | undefined,
  workspaces: Workspace[],
): string | undefined {
  if (!shortcut) return undefined
  if (shortcut.organization_id) return shortcut.organization_id
  for (const ws of workspaces) {
    if (shortcutBelongsToWorkspace(shortcut, ws)) return ws.id
  }
  return undefined
}

/** Default space when API returns none (backward compat) */
export function getSpacesForWorkspace(_wsId: string, spaces: Space[] | undefined): Array<{ id: string; name: string; path: string }> {
  if (spaces && spaces.length > 0) return spaces
  return [{ id: 'default', name: 'Default', path: '' }]
}

/** Helper to get icon for document type / kind (canonical resolver). */
export const getDocTypeIcon = (docType: string) =>
  getDocumentDisplayIcon(
    { type: docType, kind: docType },
    { className: 'w-3.5 h-3.5 flex-none' },
  )

/** Returns the sidebar icon for a shortcut (folder pin or document type). */
export const getShortcutIcon = (shortcut: SidebarShortcut) => {
  if (shortcut.folder_path) {
    return <Folder className="w-3.5 h-3.5 flex-none text-amber-500" />
  }
  return getDocTypeIcon(shortcut.document_type ?? '')
}
